import React, { useEffect, useRef, useState } from "react";
import { Button, Card, Form } from "react-bootstrap";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import SetPageTitle from "../../../components/SetPageTitle";
import { useGetPoolbonusQuery } from "../../../app/features/apiSlice";
import Loader from "../../../components/Loader";
import Error from "../../../components/Error";
import { formatNumberToLocal } from "../../../helpers";
import moment from "moment";
import "moment/locale/it";
import { useDispatch } from "react-redux";
import { setPageTitle } from "../../../app/features/DataSlice";

const PoolBonus = () => {
  const dt = useRef();
  const { t } = useTranslation();
  const { lang } = useParams();
  const dispatch = useDispatch()
  const exportCSV = () => dt.current.exportCSV();
  const sessionId = useRef(Date.now()).current;
  const [globalFilter, setGlobalFilter] = useState("");
  const [tableData, setTableData] = useState([]);

  moment.locale(lang === "it" ? "it" : "en");

  const { data, isLoading, isError, error, isSuccess } = useGetPoolbonusQuery(
    {
      sessionId,
      lang
    }
  );

  useEffect(() => {
    if (isSuccess && data?.data?.poolBonusData) {
      const tableItems = [];
      if (data?.data?.poolBonusData.length > 0) {
        data?.data?.poolBonusData.forEach((item) => {
          tableItems.push({
            userId: item?.userId,
            amount: "CHF " + formatNumberToLocal(item?.value),
            type: item.type,
            createdAt: item.createdAt,
            updatedAt: item.updatedAt,
          });
        });
      }
      setTableData(tableItems);
    }
  }, [isSuccess, data]);

  useEffect(() => {
    dispatch(setPageTitle(t(`poolBonus`)));
    // eslint-disable-next-line
  }, [t]);

  if (isLoading) {
    return <Loader />;
  }

  if (isError) {
    return <Error error={error} />;
  }

  return (
    <Card bg="dark1 text-light">
      <Card.Header
        className="d-flex flex-column flex-lg-row align-items-stretch align-items-lg-center gap-3"
      >
        {/* Page title */}
        <div className="flex-shrink-0">
          <SetPageTitle title="poolBonus" />
        </div>

        {/* Bonus totals */}
        <div className="d-flex flex-column flex-sm-row flex-lg-column justify-content-center gap-2 gap-sm-4 gap-lg-1 flex-grow-1 text-start text-lg-center">
          <div>
            {t("totalGoldBonus")}:{" "}
            <span className="text-nowrap">
              CHF {formatNumberToLocal(data?.data?.totalGoldBonus || 0)}
            </span>
          </div>

          <div>
            {t("totalSilverBonus")}:{" "}
            <span className="text-nowrap">
              CHF {formatNumberToLocal(data?.data?.totalSilverBonus || 0)}
            </span>
          </div>
        </div>

        {/* Actions */}
        <div className="d-flex flex-column flex-sm-row align-items-stretch align-items-sm-center gap-2 flex-shrink-0">
          <Button
            variant="icon-button text-light border border-light"
            onClick={() => exportCSV(false)}
            data-pr-tooltip="CSV"
            className="flex-shrink-0"
          >
            <i className="fa fa-download" />
          </Button>

          <Form.Control
            type="text"
            placeholder="Search"
            size="sm"
            className="pool-bonus-search"
            onInput={(e) => setGlobalFilter(e.target.value)}
          />
        </div>
      </Card.Header>
      <Card.Body>
        <DataTable
          value={tableData}
          sortMode="multiple"
          paginator
          rows={10}
          rowsPerPageOptions={[10, 25, 50]}
          ref={dt}
          size="small"
          showGridlines
          stripedRows
          removableSort
          globalFilter={globalFilter}
        >
          <Column
            sortable
            field="type"
            header={t("bonus")}
            body={(rowData) => t(`${rowData.type}`)}
          />

          <Column
            sortable
            field="amount"
            header={t("amount")}
          />

          <Column
            sortable
            field="createdAt"
            header={t("createdAt")}
            body={(rowData) => moment(rowData.createdAt).fromNow()}
          />

          <Column
            sortable
            field="updatedAt"
            header={t("updatedAt")}
            body={(rowData) => moment(rowData.updatedAt).fromNow()}
          />

          <Column />
        </DataTable>
      </Card.Body>
    </Card>
  );
};

export default PoolBonus;
